import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ArrowUpRight, ChevronRight } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_f59c783b-4061-4c90-8631-f537af31e122/artifacts/nm25t9qa_Calcutta%20Logo.png";

function fmtINR(n) {
  if (n === 0) return "₹0";
  return "₹" + n.toLocaleString("en-IN");
}

const WORKFLOW = ["Create", "Document", "Prove", "Protect", "Licence", "Earn"];

const PERSONAS = [
  { role: "The Programmer", body: "You wrote software nobody else had written. Keep its history." },
  { role: "The Musician", body: "You recorded something at 2 AM. Document the original." },
  { role: "The Inventor", body: "Your prototype may become a company. Preserve the evidence." },
  { role: "The Writer", body: "Your manuscript went through 47 versions. Calcutta remembers them." },
  { role: "The Designer", body: "Your drawings became a product. Track their provenance." },
  { role: "The Scientist", body: "Your notebook became a discovery. Organise its intellectual history." },
  { role: "The Startup", body: "Your company owns far more than its incorporation documents. Know what it owns." },
];

const CAPABILITIES = [
  {
    title: "IP Vault",
    body: "A private, structured record of every creation — assets, contributors, ownership, evidence, filings, contracts.",
  },
  {
    title: "Creation Timeline",
    body: "A chronological, exportable provenance history for every asset. Concept → prototype → publication → filing → licence.",
  },
  {
    title: "Version Provenance",
    body: "Every uploaded file is versioned and hashed. Nothing is silently overwritten. Compare V1 → V2 → V3 side by side.",
  },
  {
    title: "Creator Proof Pack",
    body: "A downloadable dossier organising declarations, evidence and history. Evidence-management — not a substitute for statutory registration.",
  },
  {
    title: "Licence Builder & Deal Rooms",
    body: "Draft licences in plain language. Share IP under controlled, expiring access with investors, studios, licensees and counsel.",
  },
  {
    title: "India & the World",
    body: "India-first guidance and workflows. Global usable from Day 1 — request assistance for jurisdictions we don't yet automate.",
  },
];

export default function Landing() {
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [billing, setBilling] = useState("annual");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/pricing");
        setPricing(data);
      } catch {}
    })();
  }, []);

  const enterDemo = () => { window.location.href = "/demo"; };

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-slate-900" data-testid="landing-page" style={{fontFamily:"'IBM Plex Sans', -apple-system, sans-serif"}}>
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-[#FBF8F3]/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src={LOGO} alt="Calcutta" className="w-9 h-9 rounded-sm" />
            <div className="leading-none">
              <div className="font-serif text-lg font-semibold tracking-tight">Calcutta</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">by Mahakali Tribunal</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-700">
            <a href="#vault" className="hover:text-slate-900">Vault</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#jurisdictions" className="hover:text-slate-900">Jurisdictions</a>
            <Link to="/gallery" className="hover:text-slate-900">Public IP</Link>
            <Link to="/assistance" className="hover:text-slate-900">Global Assistance</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-700 hover:text-[#B33A3A] font-medium" data-testid="landing-login">
              Sign in
            </Link>
            <button onClick={enterDemo} className="text-sm bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm font-medium transition-colors" data-testid="landing-try-demo">
              Try the demo
            </button>
          </div>
        </div>
      </header>

      {/* Hero — editorial */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 pt-20 md:pt-28 pb-20 md:pb-24">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-medium">
            Intellectual Property Infrastructure · India first · Available globally
          </div>
          <h1 className="font-serif font-semibold tracking-tight leading-[1.02] mt-6 text-slate-900" style={{fontSize:"clamp(2.75rem, 7vw, 5.75rem)"}}>
            Your ideas deserve<br/>a memory.
          </h1>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-slate-700 max-w-2xl font-serif">
            Calcutta helps creators document, organise, protect and commercialise
            intellectual property — from a screenplay to an algorithm, a drawing to
            a discovery.
          </p>
          <p className="mt-4 text-sm text-slate-500 max-w-2xl">
            Create it. Document it. Prove it. Protect it. Licence it. Earn from it.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-10">
            <button onClick={enterDemo} className="bg-[#B33A3A] hover:bg-[#A02538] text-white text-sm px-5 py-2.5 rounded-sm font-medium transition-colors flex items-center gap-2" data-testid="landing-cta-primary">
              Add your first creation <ArrowUpRight size={15} />
            </button>
            <button onClick={() => nav("/login")} className="text-sm text-slate-700 hover:text-slate-900 font-medium px-5 py-2.5 border border-slate-300 rounded-sm hover:border-slate-900 transition-colors" data-testid="landing-cta-signup">
              Create account
            </button>
            <a href="#pricing" className="text-sm text-slate-500 hover:text-slate-900 px-2">
              See pricing →
            </a>
          </div>
        </div>

        {/* Workflow line */}
        <div className="mt-24 md:mt-28 pt-10 border-t border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-6">From idea to intellectual property</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 font-serif text-2xl md:text-3xl text-slate-900">
            {WORKFLOW.map((step, i) => (
              <React.Fragment key={step}>
                <span>{step}</span>
                {i < WORKFLOW.length - 1 && <span className="text-slate-300 mx-1">·</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities — editorial two-column */}
      <section id="vault" className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-[280px_1fr] gap-10 md:gap-16">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">What Calcutta is</div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-3 leading-tight">
                A working archive for human creativity.
              </h2>
              <p className="text-sm text-slate-600 mt-5 leading-relaxed">
                Not another AI content machine. Not a fake law firm. A trusted record and
                commercialisation infrastructure where the creator remains the principal
                decision-maker.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
              {CAPABILITIES.map((c) => (
                <div key={c.title}>
                  <h3 className="font-serif text-xl font-semibold">{c.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <div className="max-w-2xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Who Calcutta is for</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-3">
              Real creators. Real records.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {PERSONAS.map((p) => (
              <div key={p.role} className="border-l-2 border-slate-900 pl-5">
                <div className="font-serif text-xl font-semibold">{p.role}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — INR, editorial table style */}
      <section id="pricing" className="bg-[#FBF8F3]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Pricing · Indian Rupees</div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-3 leading-tight">
                Affordable at every step of a creator's life.
              </h2>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed max-w-xl">
                India-first pricing. International users can subscribe in INR and request
                jurisdiction-specific assistance by email. Government / statutory fees and
                independent legal fees are billed at cost — never bundled silently.
              </p>
            </div>
            <div className="inline-flex items-center gap-1 border border-slate-300 rounded-sm p-1 bg-white" data-testid="billing-toggle">
              {["monthly", "annual"].map((m) => (
                <button
                  key={m}
                  onClick={() => setBilling(m)}
                  className={`px-4 py-1.5 text-[11px] uppercase tracking-widest font-semibold rounded-sm transition-colors ${
                    billing === m ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`billing-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border border-slate-300 bg-white">
            {(pricing?.tiers || []).map((t, i) => {
              const price = billing === "annual" ? t.price_annual_inr : t.price_monthly_inr;
              const period = billing === "annual" ? "/year" : "/month";
              const isFeatured = t.badge === "Most popular";
              return (
                <div
                  key={t.id}
                  data-testid={`tier-${t.id}`}
                  className={`p-6 border-slate-200 ${i > 0 ? "md:border-l" : ""} ${isFeatured ? "bg-slate-900 text-white" : ""} relative`}
                >
                  {t.badge && (
                    <span className={`absolute top-4 right-4 text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm ${
                      isFeatured ? "bg-[#B33A3A] text-white" : "bg-slate-100 text-slate-700 border border-slate-300"
                    }`}>{t.badge}</span>
                  )}
                  <div className={`text-[10px] uppercase tracking-[0.2em] ${isFeatured ? "text-white/60" : "text-slate-500"}`}>{t.id}</div>
                  <h3 className={`font-serif text-2xl font-semibold mt-1 ${isFeatured ? "text-white" : ""}`}>{t.name}</h3>
                  <p className={`text-xs mt-2 leading-relaxed ${isFeatured ? "text-white/70" : "text-slate-500"}`}>{t.tagline}</p>
                  <div className={`mt-6 mb-5 pb-5 border-b ${isFeatured ? "border-white/20" : "border-slate-200"}`}>
                    <span className="font-serif text-3xl font-semibold tracking-tight">{fmtINR(price)}</span>
                    {price > 0 && <span className={`text-xs ml-1 ${isFeatured ? "text-white/60" : "text-slate-500"}`}>{period}</span>}
                    {price === 0 && <span className={`text-xs ml-1 ${isFeatured ? "text-white/60" : "text-slate-500"}`}>forever</span>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {t.features.map((f) => (
                      <li key={f} className={`text-xs leading-relaxed ${isFeatured ? "text-white/85" : "text-slate-700"}`}>
                        · {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => nav("/login")}
                    data-testid={`tier-cta-${t.id}`}
                    className={`w-full py-2 rounded-sm text-[11px] uppercase tracking-widest font-semibold transition-colors ${
                      isFeatured ? "bg-[#B33A3A] hover:bg-[#A02538] text-white"
                      : "border border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900"
                    }`}
                  >
                    {t.id === "organisation" ? "Contact us" : t.id === "free" ? "Start free" : "Choose"}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Pay-as-you-go */}
          {pricing?.pay_as_you_go && (
            <div className="mt-12">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-4">Pay as you go — no subscription required</div>
              <div className="border border-slate-300 bg-white divide-y divide-slate-200" data-testid="payg-list">
                {pricing.pay_as_you_go.map((p) => (
                  <div key={p.label} className="flex items-center justify-between py-3 px-5">
                    <div className="text-sm text-slate-800">{p.label}</div>
                    <div className="font-mono text-sm text-slate-700">
                      {p.price_from_inr ? `from ${fmtINR(p.price_from_inr)}` : (p.note || "Quote")}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3 max-w-2xl">
                {pricing.disclaimer}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Jurisdictions table */}
      <section id="jurisdictions" className="bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <div className="max-w-2xl mb-10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Filing assistance — per jurisdiction</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mt-3">
              Transparent fees. India first. Rest of the world by request.
            </h2>
            <p className="text-sm text-slate-600 mt-4 leading-relaxed">
              Prices below are Calcutta's preparation & documentation charges in INR. Government fees, translation, and independent counsel are billed at cost.
            </p>
          </div>
          <div className="border border-slate-300 overflow-hidden">
            <table className="w-full text-sm" data-testid="jurisdiction-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">Jurisdiction</th>
                  <th className="py-3 px-4 text-right font-semibold">Patent</th>
                  <th className="py-3 px-4 text-right font-semibold">Trademark</th>
                  <th className="py-3 px-4 text-right font-semibold">Copyright</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pricing && Object.entries(pricing.jurisdictions).map(([code, fees]) => (
                  <tr key={code} className={code === "IN" ? "bg-[#FBF8F3]" : ""}>
                    <td className="py-3 px-4 font-mono font-semibold">
                      {code}{code === "IN" && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#B33A3A]">Home</span>}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">{fmtINR(fees.patent)}</td>
                    <td className="py-3 px-4 text-right font-mono">{fmtINR(fees.trademark)}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-600">{fmtINR(fees.copyright)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-slate-500 max-w-xl">
              Not seeing your jurisdiction? Calcutta operates India-first — for other countries our team responds by email.
            </p>
            <Link to="/assistance" className="text-sm text-slate-900 hover:text-[#B33A3A] font-medium border-b border-slate-900 hover:border-[#B33A3A]" data-testid="global-assistance-link">
              Request global assistance →
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer / trust block */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">What we are</div>
              <p className="mt-3 text-sm leading-relaxed text-white/85">
                Evidence, timeline, licensing and provenance infrastructure for creators and companies.
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">What we are not</div>
              <p className="mt-3 text-sm leading-relaxed text-white/85">
                Not a government IP registry. Not a substitute for professional legal advice. A Calcutta Asset ID is an organisational identifier — not a statutory registration number.
              </p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">How we use AI</div>
              <p className="mt-3 text-sm leading-relaxed text-white/85">
                Quietly, where it saves you work — classification, summaries, drafting help. Never as a substitute for your decisions or professional review.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FBF8F3]">
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-20 md:py-24 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-slate-900">
            Your work is yours.<br/>Calcutta helps you remember it.
          </h2>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={enterDemo} className="bg-[#B33A3A] hover:bg-[#A02538] text-white px-6 py-3 rounded-sm text-sm font-medium transition-colors flex items-center gap-2" data-testid="landing-cta-bottom">
              Try the demo <ChevronRight size={14} />
            </button>
            <Link to="/login" className="text-slate-900 hover:bg-slate-900 hover:text-white px-5 py-3 text-sm font-medium border border-slate-900 rounded-sm transition-colors">
              Start free
            </Link>
            <Link to="/gallery" className="text-slate-700 hover:text-slate-900 px-5 py-3 text-sm font-medium">
              Browse public IP →
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <img src={LOGO} alt="" className="w-6 h-6 rounded-sm" />
              <span>Calcutta · by Mahakali Tribunal · Intelligence · Justice · Wealth</span>
            </div>
            <div className="flex items-center gap-5">
              <Link to="/gallery" className="hover:text-slate-900">Public gallery</Link>
              <Link to="/assistance" className="hover:text-slate-900">Global assistance</Link>
              <a href="#pricing" className="hover:text-slate-900">Pricing</a>
              <Link to="/login" className="hover:text-slate-900">Sign in</Link>
            </div>
          </div>
          <div className="text-[10px] text-slate-400 mt-4 max-w-2xl">
            © {new Date().getFullYear()} Mahakali Tribunal. Calcutta is intellectual-property record & commercialisation software. It is not a law firm and does not provide legal advice. A Calcutta Asset ID is not a government-issued registration number.
          </div>
        </div>
      </footer>
    </div>
  );
}
